import { 
  uploadProductImageToCloudinary, 
  uploadMemberPhotoToCloudinary, 
  uploadReceiptToCloudinary,
  CLOUDINARY_FOLDERS
} from './cloudinary';

export const GDRIVE_CONFIG = {
  ahliServiceAccount: "silat-ahli@amazing-modem-140010.iam.gserviceaccount.com",
  managementServiceAccount: "silat-management@amazing-modem-140010.iam.gserviceaccount.com",
  ahliFolderId: "gdrive_folder_ahli_asmm_photos",
  managementFolderId: "gdrive_folder_management_audit_silibus",
  ahliJsonPath: "E:\\Silat\\Dokumen Rasmi\\Detail Persatuan\\gdrive-ahli.json",
  managementJsonPath: "E:\\Silat\\Dokumen Rasmi\\Detail Persatuan\\gdrive-management.json"
};

export interface DriveFileItem {
  id: string;
  name: string;
  mimeType: string;
  driveType: 'ahli' | 'management';
  webContentLink: string;
  category: 'Audit Receipt' | 'Syllabus PDF' | 'Instructional Video' | 'Member Photo' | 'Meeting Minutes' | 'Public Poster' | 'General Attachment';
  uploadedAt: string;
  size: string;
  memberDirectory?: string; // Single member directory path e.g. ASMM/Ahli/850612025431/
  memberIcNo?: string;
}

/**
 * Returns single member Google Drive directory path stored inside gdrive-ahli.json
 */
export function getMemberDriveDirectory(icNo: string): string {
  const cleanIc = (icNo || 'general').replace(/[^0-9a-zA-Z_]/g, '');
  return `ASMM/Ahli/${cleanIc}/`;
}

/**
 * Returns the appropriate Google Drive Service Account vault type ('ahli' vs 'management')
 * to ensure strict data security and prevent confidential management/audit data leakage.
 */
export function getDriveVaultForCategory(category: DriveFileItem['category']): 'ahli' | 'management' {
  switch (category) {
    case 'Audit Receipt':
    case 'Syllabus PDF':
    case 'Instructional Video':
    case 'Meeting Minutes':
      return 'management'; // Uses gdrive-management.json (Confidential Vault)
    case 'Member Photo':
    case 'Public Poster':
    case 'General Attachment':
    default:
      return 'ahli'; // Uses gdrive-ahli.json (Public / Member Vault)
  }
}

export function getDriveServiceAccountConfig(vault: 'ahli' | 'management') {
  if (vault === 'management') {
    return {
      serviceAccount: GDRIVE_CONFIG.managementServiceAccount,
      folderId: GDRIVE_CONFIG.managementFolderId,
      jsonPath: GDRIVE_CONFIG.managementJsonPath
    };
  }
  return {
    serviceAccount: GDRIVE_CONFIG.ahliServiceAccount,
    folderId: GDRIVE_CONFIG.ahliFolderId,
    jsonPath: GDRIVE_CONFIG.ahliJsonPath
  };
}

/**
 * Official ASMM management documents.
 * webContentLink should point to Google Drive shareable links or public/ folder paths.
 * TODO: Replace placeholder Drive links with real Google Drive file IDs once uploaded.
 */
export const INITIAL_MANAGEMENT_DRIVE_FILES: DriveFileItem[] = [
  {
    id: "gdrive-doc-06",
    name: "PERLEMBAGAAN PERSATUAN SENI SILAT ASMM 2007.pdf",
    mimeType: "application/pdf",
    driveType: "management",
    webContentLink: "/docs/PERLEMBAGAAN-PERSATUAN-SENI-SILAT-ASMM-2007.pdf",
    category: "Syllabus PDF",
    uploadedAt: "2024-12-03",
    size: "223.7 KB"
  },
  {
    id: "gdrive-doc-03",
    name: "SILIBUS Pembelajaran & Peringkat Bengkung PSSASMM.pdf",
    mimeType: "application/pdf",
    driveType: "management",
    webContentLink: "/docs/SILIBUS-Pembelajaran-Peringkat-Bengkung-PSSASMM.pdf",
    category: "Syllabus PDF",
    uploadedAt: "2025-01-08",
    size: "142.4 KB"
  },
  {
    id: "gdrive-doc-04",
    name: "SANDANG KEBESARAN PSSASMM_V4.pdf",
    mimeType: "application/pdf",
    driveType: "management",
    webContentLink: "/docs/SANDANG-KEBESARAN-PSSASMM-V4.pdf",
    category: "Syllabus PDF",
    uploadedAt: "2024-12-03",
    size: "302.4 KB"
  },
  {
    id: "gdrive-doc-02",
    name: "Sijil Kelulusan Permohonan Induk ROS Kedah.pdf",
    mimeType: "application/pdf",
    driveType: "management",
    webContentLink: "/docs/Sijil-Kelulusan-Permohonan-Induk-ROS.pdf",
    category: "Syllabus PDF",
    uploadedAt: "2024-08-28",
    size: "56.9 KB"
  }
];

export function compressImage(file: File, maxWidth = 200, maxHeight = 200): Promise<File> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
        }

        canvas.toBlob((blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(compressedFile);
          } else {
            resolve(file);
          }
        }, 'image/jpeg', 0.75);
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Uploads member profile photo to Google Drive Vault (gdrive-ahli.json)
 * and stores it inside each member's dedicated single directory e.g. ASMM/Ahli/850612025431/
 */
export async function uploadMemberPhotoToAhliDriveJson(file: File, memberIcNo: string, memberName: string): Promise<string> {
  let compressedFile = file;
  try {
    compressedFile = await compressImage(file);
  } catch (e) {
    console.warn('Image compression failed, using original file:', e);
  }

  const cleanIc = (memberIcNo || 'general').replace(/[^0-9a-zA-Z_]/g, '');
  const singleDir = getMemberDriveDirectory(cleanIc);
  const fileId = `cld-member-${cleanIc}-${Date.now()}`;
  const fileSizeKB = (compressedFile.size / 1024).toFixed(1) + ' KB';
  const fileDate = new Date().toISOString().split('T')[0];

  let uploadedUrl = '';
  try {
    const cld = await uploadMemberPhotoToCloudinary(compressedFile, cleanIc, memberName);
    uploadedUrl = cld.secureUrl;
  } catch (err) {
    console.warn('Cloudinary member photo upload notice, falling back to local reader:', err);
  }

  return new Promise((resolve, reject) => {
    const finalizeWithUrl = (finalUrl: string) => {
      const newDriveItem: DriveFileItem = {
        id: fileId,
        name: `profile-photo-${cleanIc}.jpg`,
        mimeType: 'image/jpeg',
        driveType: 'ahli',
        webContentLink: finalUrl,
        category: 'Member Photo',
        uploadedAt: fileDate,
        size: fileSizeKB,
        memberDirectory: singleDir,
        memberIcNo: cleanIc
      };

      try {
        const rawAhliVault = localStorage.getItem('gdrive_ahli_json_vault');
        const ahliVaultList: DriveFileItem[] = rawAhliVault ? JSON.parse(rawAhliVault) : [];
        const filteredVault = ahliVaultList.filter(f => f.memberIcNo !== cleanIc);
        const updatedVault = [newDriveItem, ...filteredVault];
        localStorage.setItem('gdrive_ahli_json_vault', JSON.stringify(updatedVault));
      } catch (e) {
        console.warn('Failed updating gdrive_ahli_json_vault cache:', e);
      }

      try {
        const rawFiles = localStorage.getItem('asmm_drive_files');
        const fileList: DriveFileItem[] = rawFiles ? JSON.parse(rawFiles) : [];
        const filteredFiles = fileList.filter(f => f.memberIcNo !== cleanIc);
        localStorage.setItem('asmm_drive_files', JSON.stringify([newDriveItem, ...filteredFiles]));
      } catch (e) {
        console.warn('Failed updating asmm_drive_files cache:', e);
      }

      resolve(finalUrl);
    };

    if (uploadedUrl) {
      finalizeWithUrl(uploadedUrl);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      finalizeWithUrl(reader.result as string);
    };
    reader.onerror = () => {
      reject(new Error('Gagal membaca fail gambar profil.'));
    };
    reader.readAsDataURL(compressedFile);
  });
}

export interface UploadedReceiptResult {
  fileName: string;
  fileUrl: string;
  driveItem: DriveFileItem;
}

/**
 * Generates the standardized receipt filename: memberid_name_month_year.pdf
 * Example: asmm-001_Mohd_Hafife_September_2026.pdf
 */
export function formatReceiptPdfFileName(
  memberId: string,
  memberName: string,
  month: string,
  year: string | number
): string {
  const cleanId = (memberId || 'asmm-001').replace(/[^a-zA-Z0-9_-]/g, '');
  const cleanName = (memberName || 'Ahli')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '');
  const cleanMonth = (month || 'Bulan').trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
  const cleanYear = String(year || new Date().getFullYear()).replace(/[^0-9]/g, '');
  return `${cleanId}_${cleanName}_${cleanMonth}_${cleanYear}.pdf`;
}

/**
 * Uploads payment receipt to Google Drive Ahli Vault (gdrive-ahli.json)
 * and saves as memberid_name_month_year.pdf inside member directory ASMM/Ahli/<ic_or_id>/
 */
export async function uploadYuranReceiptToAhliDriveJson(
  file: File,
  memberId: string,
  memberName: string,
  month: string,
  year: string | number,
  memberIcNo?: string
): Promise<UploadedReceiptResult> {
  const extension = (file.name.split('.').pop() || (file.type.includes('pdf') ? 'pdf' : 'png')).toLowerCase();
  const basePdfName = formatReceiptPdfFileName(memberId, memberName, month, year);
  const fileName = extension !== 'pdf'
    ? basePdfName.replace(/\.pdf$/i, `.${extension}`)
    : basePdfName;
  const cleanIc = (memberIcNo || memberId || 'general').replace(/[^0-9a-zA-Z_]/g, '');
  const singleDir = getMemberDriveDirectory(cleanIc);
  const fileId = `cld-receipt-${cleanIc}-${Date.now()}`;
  const fileSizeKB = (file.size / 1024).toFixed(1) + ' KB';
  const fileDate = new Date().toISOString().split('T')[0];

  let uploadedUrl = '';
  try {
    const cld = await uploadReceiptToCloudinary(file, `${cleanIc}-${fileName.replace(/\.[^/.]+$/, "")}`);
    uploadedUrl = cld.secureUrl;
  } catch (err) {
    console.warn('Cloudinary receipt upload notice, falling back to local reader:', err);
  }

  return new Promise((resolve, reject) => {
    const finalizeWithUrl = (finalUrl: string) => {
      const newDriveItem: DriveFileItem = {
        id: fileId,
        name: fileName,
        mimeType: file.type.includes('pdf') ? 'application/pdf' : (file.type || 'image/png'),
        driveType: 'ahli',
        webContentLink: finalUrl,
        category: 'Audit Receipt',
        uploadedAt: fileDate,
        size: fileSizeKB,
        memberDirectory: singleDir,
        memberIcNo: cleanIc
      };

      try {
        const rawAhliVault = localStorage.getItem('gdrive_ahli_json_vault');
        const ahliVaultList: DriveFileItem[] = rawAhliVault ? JSON.parse(rawAhliVault) : [];
        const updatedVault = [newDriveItem, ...ahliVaultList.filter(f => f.name !== fileName)];
        localStorage.setItem('gdrive_ahli_json_vault', JSON.stringify(updatedVault));
      } catch (e) {
        console.warn('Failed updating gdrive_ahli_json_vault cache:', e);
      }

      try {
        const rawFiles = localStorage.getItem('asmm_drive_files');
        const fileList: DriveFileItem[] = rawFiles ? JSON.parse(rawFiles) : [];
        const updatedFiles = [newDriveItem, ...fileList.filter(f => f.name !== fileName)];
        localStorage.setItem('asmm_drive_files', JSON.stringify(updatedFiles));
      } catch (e) {
        console.warn('Failed updating asmm_drive_files cache:', e);
      }

      resolve({
        fileName,
        fileUrl: finalUrl,
        driveItem: newDriveItem
      });
    };

    if (uploadedUrl) {
      finalizeWithUrl(uploadedUrl);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      finalizeWithUrl(reader.result as string);
    };
    reader.onerror = () => {
      reject(new Error('Gagal membaca fail resit bayaran.'));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Compresses product image while preserving high-definition crisp details (800x800, 85% quality, ~70-120KB).
 */
export function compressProductImage(file: File, maxWidth = 800, maxHeight = 800, quality = 0.85): Promise<File> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);
        }

        canvas.toBlob((blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(compressedFile);
          } else {
            resolve(file);
          }
        }, 'image/jpeg', quality);
      };
      img.onerror = () => resolve(file);
      img.src = e.target?.result as string;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
}

/**
 * Uploads merchandise product image to Google Drive Management Vault (gdrive-management.json)
 * and records it in Drive files database with high-resolution clarity.
 */
export async function uploadProductImageToDrive(
  file: File,
  productId?: string,
  productName?: string
): Promise<{ dataUrl: string; driveItem: DriveFileItem }> {
  let compressedFile = file;
  try {
    compressedFile = await compressProductImage(file, 1400, 1400, 0.90);
  } catch (e) {
    console.warn('Image compression failed, using original file:', e);
  }

  const cleanId = (productId || 'new-product').replace(/[^0-9a-zA-Z_-]/g, '');
  const cleanName = (productName || 'product')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^0-9a-zA-Z_-]/g, '');
  const fileId = `cld-merch-${cleanId}-${Date.now()}`;
  const fileSizeKB = (compressedFile.size / 1024).toFixed(1) + ' KB';
  const fileDate = new Date().toISOString().split('T')[0];
  const extension = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const fileName = `merch-${cleanName}-${Date.now()}.${extension}`;
  const targetDir = `ASMM/Merchandise/${cleanId}/`;

  let uploadedUrl = '';
  try {
    const cld = await uploadProductImageToCloudinary(compressedFile, cleanId, cleanName);
    uploadedUrl = cld.secureUrl;
  } catch (err) {
    console.warn('Cloudinary merchandise upload notice, falling back to local reader:', err);
  }

  return new Promise((resolve, reject) => {
    const finalizeWithUrl = (finalUrl: string) => {
      const newDriveItem: DriveFileItem = {
        id: fileId,
        name: fileName,
        mimeType: compressedFile.type || 'image/jpeg',
        driveType: 'management',
        webContentLink: finalUrl,
        category: 'Public Poster',
        uploadedAt: fileDate,
        size: fileSizeKB,
        memberDirectory: targetDir
      };

      try {
        const rawVault = localStorage.getItem('gdrive_management_json_vault');
        const vaultList: DriveFileItem[] = rawVault ? JSON.parse(rawVault) : [];
        const updatedVault = [newDriveItem, ...vaultList.filter(f => f.id !== fileId)];
        localStorage.setItem('gdrive_management_json_vault', JSON.stringify(updatedVault));
      } catch (e) {
        console.warn('Failed updating gdrive_management_json_vault cache:', e);
      }

      try {
        const rawFiles = localStorage.getItem('asmm_drive_files');
        const fileList: DriveFileItem[] = rawFiles ? JSON.parse(rawFiles) : [];
        const updatedFiles = [newDriveItem, ...fileList.filter(f => f.id !== fileId)];
        localStorage.setItem('asmm_drive_files', JSON.stringify(updatedFiles));
      } catch (e) {
        console.warn('Failed updating asmm_drive_files cache:', e);
      }

      resolve({
        dataUrl: finalUrl,
        driveItem: newDriveItem
      });
    };

    if (uploadedUrl) {
      finalizeWithUrl(uploadedUrl);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      finalizeWithUrl(reader.result as string);
    };
    reader.onerror = () => {
      reject(new Error('Gagal membaca fail gambar produk.'));
    };
    reader.readAsDataURL(compressedFile);
  });
}

